import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, ImagePlus, Check, CheckCheck, Loader2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { uploadImage, IMAGE_ACCEPT } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";

function clock(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function Chat() {
  const { convId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const fileRef = useRef();
  const scrollRef = useRef();
  const typingRef = useRef(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const { data } = useQuery({
    queryKey: ["messages", convId],
    queryFn: async () => (await api.get(`/conversations/${convId}/messages`)).data,
    refetchInterval: 2500,
  });

  const messages = data?.items || [];
  const other = data?.conversation?.other_user;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, data?.other_typing]);

  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - typingRef.current > 3000) {
      typingRef.current = now;
      api.post(`/conversations/${convId}/typing`).catch(() => {});
    }
  }, [convId]);

  const send = async (payload) => {
    setSending(true);
    try {
      await api.post(`/conversations/${convId}/messages`, payload);
      setText("");
      qc.invalidateQueries({ queryKey: ["messages", convId] });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const onSend = () => { if (text.trim()) send({ text: text.trim() }); };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only images are allowed");
    setSending(true);
    try { const { url } = await uploadImage(file); await send({ image: url }); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); setSending(false); }
  };

  return (
    <div className="flex h-screen flex-col">
      {/* Header — no video/call icon */}
      <div className="glass flex items-center gap-3 px-3 pb-3 pt-4">
        <button onClick={() => nav("/messages")} data-testid="chat-back" className="pressable flex h-10 w-10 items-center justify-center rounded-full">
          <ArrowLeft size={19} />
        </button>
        <button onClick={() => other?.id && nav(`/u/${other.id}`)} data-testid="chat-profile" className="pressable flex flex-1 items-center gap-3 text-left">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-accent text-white" style={{ minWidth: 40 }}>
            {other?.avatar ? <img src={fileUrl(other.avatar)} alt="" className="h-full w-full object-cover" /> : <span className="font-bold">{(other?.name?.[0] || "?").toUpperCase()}</span>}
          </div>
          <div className="flex-1">
            <p className="font-display font-bold leading-tight">{other?.name || "…"}</p>
            <div className="flex items-center gap-1.5">
              {other?.roles?.[0] && <span className="text-[11px] font-bold accent">{other.roles.join(" · ")}</span>}
              {data?.other_typing && <span className="text-[11px] font-semibold" style={{ color: "var(--green)" }}>typing…</span>}
            </div>
          </div>
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="no-scrollbar flex-1 space-y-2 overflow-y-auto px-3 py-4">
        {messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <motion.div key={m.id} initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div className="max-w-[78%] px-3 py-2" style={{
                background: mine ? "linear-gradient(135deg,#F59E0B,#D97706)" : "var(--surface-2)",
                color: mine ? "#fff" : "var(--text)",
                borderRadius: 18, borderBottomRightRadius: mine ? 4 : 18, borderBottomLeftRadius: mine ? 18 : 4,
              }}>
                {m.image && <img src={fileUrl(m.image)} alt="" className="mb-1 max-h-64 rounded-xl object-cover" />}
                {m.text && <p className="text-[15px] leading-snug">{m.text}</p>}
                <div className="mt-0.5 flex items-center justify-end gap-1">
                  <span className="text-[10px]" style={{ opacity: 0.7 }}>{clock(m.created_at)}</span>
                  {mine && (m.read ? <CheckCheck size={13} style={{ color: "#bfe9ff" }} /> : <Check size={13} style={{ opacity: 0.7 }} />)}
                </div>
              </div>
            </motion.div>
          );
        })}
        <AnimatePresence>
          {data?.other_typing && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex justify-start">
              <div className="flex gap-1 rounded-2xl px-4 py-3" style={{ background: "var(--surface-2)" }}>
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} className="h-2 w-2 rounded-full" style={{ background: "var(--text-dim)" }}
                               animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.9, delay: i * 0.15 }} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Composer */}
      <div className="glass flex items-center gap-2 px-3 py-3" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
        <button onClick={() => fileRef.current?.click()} data-testid="chat-image-btn" className="pressable flex h-10 w-10 items-center justify-center rounded-full" style={{ background: "var(--surface-2)" }}>
          <ImagePlus size={20} className="accent" />
        </button>
        <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} hidden onChange={onImage} data-testid="chat-image-input" />
        <input value={text} onChange={(e) => { setText(e.target.value); notifyTyping(); }}
               onKeyDown={(e) => e.key === "Enter" && onSend()} placeholder="Message…" data-testid="chat-input"
               className="glass flex-1 rounded-full px-4 py-2.5 text-[15px] outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
        <button onClick={onSend} disabled={sending || !text.trim()} data-testid="chat-send"
                className="btn-accent flex h-11 w-11 items-center justify-center rounded-full disabled:opacity-50">
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
}
